import { IonIcon } from '@ionic/react';
import {
  alertCircleOutline,
  checkmarkCircleOutline,
  musicalNotesOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useIcons } from '../../hooks/useIcons';
import {
  getAgentTransfer,
  subscribeToAgentTransfer,
} from '../../services/AgentTransferFeed.service';
import { SERVICE_NAMES, StreamingExportProgress } from '../../types/Streaming';
import './StreamingTransferCard.scss';

/**
 * The agent's streaming export runs for minutes, so the chat shows where the
 * tracks are going while it happens rather than a silent tool line.
 */

const DESTINATION_ICONS: Record<StreamingExportProgress['service'], string> = {
  spotify: 'spotify',
  tidal: 'tidal',
  apple: 'apple',
};

const PHASE_LABELS: Record<StreamingExportProgress['phase'], string> = {
  matching: 'Matching tracks',
  creating: 'Creating playlist',
  adding: 'Adding tracks',
  done: 'Transfer complete',
  error: 'Transfer failed',
};

/** Matching is the long pass, so it owns most of the bar. */
const percentFor = (progress: StreamingExportProgress): number => {
  if (progress.phase === 'done') return 100;
  if (progress.phase === 'adding') return 95;
  if (progress.phase === 'creating') return 90;
  if (!progress.total) return 0;
  return Math.round((progress.matched / progress.total) * 85);
};

export const StreamingTransferCard: React.FC = () => {
  const { icons } = useIcons();
  const [transfer, setTransfer] = useState<StreamingExportProgress | null>(
    getAgentTransfer,
  );

  useEffect(() => subscribeToAgentTransfer(setTransfer), []);

  if (!transfer) return null;

  const { phase, service, playlistName, matched, total } = transfer;
  const settled = phase === 'done' || phase === 'error';
  const destinationIcon = icons[DESTINATION_ICONS[service]];

  return (
    <div className={`streaming-transfer streaming-transfer-${phase}`}>
      <div className="streaming-transfer-route">
        <div className="streaming-transfer-node">
          {icons.flameLogo ? (
            <img src={icons.flameLogo} alt="" />
          ) : (
            <IonIcon icon={musicalNotesOutline} />
          )}
          <span>Crate Hackers</span>
        </div>

        <div className="streaming-transfer-wire">
          <span className="streaming-transfer-line" />
          {!settled && (
            <span className="streaming-transfer-pulse">
              <i />
              <i />
              <i />
            </span>
          )}
          {phase === 'done' && (
            <IonIcon
              icon={checkmarkCircleOutline}
              className="streaming-transfer-verdict"
            />
          )}
          {phase === 'error' && (
            <IonIcon
              icon={alertCircleOutline}
              className="streaming-transfer-verdict"
            />
          )}
        </div>

        <div className="streaming-transfer-node">
          {destinationIcon ? (
            <img src={destinationIcon} alt="" />
          ) : (
            <IonIcon icon={musicalNotesOutline} />
          )}
          <span>{SERVICE_NAMES[service]}</span>
        </div>
      </div>

      <div className="streaming-transfer-detail">
        <div className="streaming-transfer-headline">
          <strong>{PHASE_LABELS[phase]}</strong>
          <span className="streaming-transfer-count">
            {matched}/{total}
          </span>
        </div>
        <div className="streaming-transfer-bar">
          <span style={{ width: `${percentFor(transfer)}%` }} />
        </div>
        <p className="streaming-transfer-caption">
          {transfer.currentTrack || transfer.message || playlistName}
        </p>
      </div>
    </div>
  );
};

export default StreamingTransferCard;
